import { useInfiniteQuery } from '@tanstack/react-query';
import { getBrowserClient } from '@/lib/supabase';
import { useHostessStore } from '@/stores/hostess';
import { formatDateTime, ACTIVITY_TYPE_MAP, toHostessVisibleCustomerCategoryEmoji, isKanaQuery, toKatakana, type ActivityType } from '@sakura-grp/lib';
import { getHostessProfileImageUrlOrPlaceholder } from '@/lib/storage-utils';
import type { AnnouncementActivity } from '@/lib/api/announcements';

const PAGE_SIZE = 50;

export interface AnnouncementsPage {
  activities: AnnouncementActivity[];
  totalRows: number;
  nextPage: number | undefined;
}

export interface AnnouncementFilters {
  showOnlyMine: boolean;
  search: string;
  storeIds: string[] | null;
  dateFrom: string | null;
  dateTo: string | null;
}

export function useAnnouncementsInfinite(filters: AnnouncementFilters) {
  const { hostess } = useHostessStore();
  const hostessId = hostess?.id;

  const searchTrimmed = filters.search.trim();
  const searchParam = searchTrimmed || undefined;
  // フリガナ検索ゲート: カナ (ひらがな/カタカナ) を含み 2 文字以上のときのみ p_search_kana を発火。
  // useTabCustomers と同じパターン。
  const searchKanaParam =
    searchTrimmed.length >= 2 && isKanaQuery(searchTrimmed)
      ? toKatakana(searchTrimmed)
      : undefined;

  return useInfiniteQuery<AnnouncementsPage>({
    queryKey: [
      'announcements',
      hostessId,
      filters.showOnlyMine,
      filters.storeIds,
      searchParam,
      searchKanaParam,
      filters.dateFrom,
      filters.dateTo,
    ],
    queryFn: async ({ pageParam }) => {
      const supabase = getBrowserClient();
      const { data, error } = await supabase.rpc('get_hostess_announcements', {
        p_hostess_id: hostessId!,
        p_show_only_mine: filters.showOnlyMine,
        p_search: searchParam ?? undefined,
        p_search_kana: searchKanaParam,
        p_store_ids: filters.storeIds ?? undefined,
        p_date_from: filters.dateFrom ?? undefined,
        p_date_to: filters.dateTo ?? undefined,
        p_page: pageParam as number,
        p_page_size: PAGE_SIZE,
      });

      if (error) throw new Error(`営業履歴取得エラー: ${error.message}`);

      const rows = data || [];
      const totalRows = rows[0]?.total_rows ?? 0;

      const activities: AnnouncementActivity[] = rows.map((row: any) => {
        const activityType = (ACTIVITY_TYPE_MAP[row.activity_type as keyof typeof ACTIVITY_TYPE_MAP] || 'メッセ') as ActivityType;

        return {
          id: row.id,
          store: row.store_name || '不明',
          type: activityType,
          date: formatDateTime(row.activity_date),
          activityDateIso: row.activity_date,
          customerType: toHostessVisibleCustomerCategoryEmoji(row.customer_type),
          customerId: row.customer_id || '',
          customerName: row.customer_name || '不明',
          company: row.company_name || undefined,
          hostess: {
            id: row.hostess_id,
            name: row.hostess_name || '不明',
            status: row.hostess_status === 'active' ? 'working' as const : 'absent' as const,
            role: row.hostess_role || undefined,
            avatar: getHostessProfileImageUrlOrPlaceholder(row.hostess_profile_image_path),
          },
          isMyActivity: row.is_my_activity,
          isRelatedToMe: true, // RPC が既にフィルタ済み
        };
      });

      const loadedSoFar = (pageParam as number) * PAGE_SIZE;
      const nextPage = loadedSoFar < totalRows ? (pageParam as number) + 1 : undefined;

      return { activities, totalRows, nextPage };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!hostessId,
  });
}
